<template>
    <hox-modal :max-width-px="700" resizable @close="close">
        <template #header>Download</template>
        <DownloadPlatformSelect
            v-model="platform"
            :show-html-options="showHtmlOptions"
            :show-psd-options="showPsdOptions"
            :show-ae-options="showAeOptions"
        />
        <Tabs v-if="platform" :model-value="tab" @onClick="onTabChange">
            <TabPane label="Download" name="downloadOptions">
                <DownloadDisclaimer :platform="platform" @tabChange="onTabChange" />
                <div class="download-modal__options-wrapper">
                    <DownloadHTMLOptions v-if="zipDownload" :download-format="platform" />
                    <DownloadVideoOptions
                        v-if="platform === DOWNLOAD_FORMAT.VIDEO && templateType === TemplateType.HTML5"
                    />
                    <DownloadAEVideoOptions
                        v-if="platform === DOWNLOAD_FORMAT.VIDEO && templateType === TemplateType.AE"
                    />
                    <DownloadExcelOptions v-if="platform === DOWNLOAD_FORMAT.FLASHTALKING_EXCEL" />
                </div>
            </TabPane>
            <TabPane label="Changes overview" name="changesOverview">
                <DownloadModalPlatformDetails :platform="platform" :template-type="templateType" />
            </TabPane>
        </Tabs>
        <template #footer>
            <Button class="download-modal__button" data-testid="asset-download__button--cancel" @click="close">
                Cancel
            </Button>
            <Button
                class="download-modal__button"
                type="primary"
                data-testid="asset-download__button--download"
                :disabled="buttonDisabled"
                @click="processDownloads"
            >
                Download
            </Button>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { mapStores } from "pinia";

import { TemplateType } from "hoxton-shared/enums/masterTemplate";
import { DOWNLOAD_FORMAT, ZIP_DOWNLOADS, type DownloadFormatType } from "hoxton-shared/enums/exports";
import { DownloadPlatformsLabels } from "@/enums/platforms";

import { NotificationTypes } from "@/enums/notifications";
import { JobStatusToNotificationStatus, JobNames } from "@/enums/jobs";
import { createAEJob } from "@/utils/ae";
import computeAfterEffectsLayers from "@/utils/computeAfterEffectsLayers";
import type { ConstructedDeliverable } from "@/types/types";
import { downloadJob } from "@/services/deliverables/publish";

import { JobsAction } from "@/store/modules/jobs";
import { NotificationsAction } from "@/store/modules/notifications";
import { useCreativeLibraryFiltersStore } from "@/store/pinia/creativeLibraryFilters";

import DownloadDisclaimer from "@/components/Download/DownloadDisclaimer.vue";
import DownloadHTMLOptions from "@/components/Download/DownloadHTMLOptions.vue";
import DownloadVideoOptions from "@/components/Download/DownloadVideoOptions.vue";
import DownloadExcelOptions from "@/components/Download/DownloadExcelOptions.vue";
import DownloadPlatformSelect from "@/components/Campaign/DownloadPlatformSelect.vue";
import DownloadModalPlatformDetails from "@/components/Download/DownloadModalPlatformDetails.vue";
import DownloadAEVideoOptions from "@/components/Download/DownloadAEVideoOptions.vue";

import { useDownload } from "@/composables/download/useDownload";
import { useParams } from "@/composables/useParams";
import { useDownloadAEVideo } from "@/composables/download/useDownloadAEVideo";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import { GA4_EVENT_TYPES, GA4_FEATURE_CATEGORIES, GA4_FEATURE_CONTEXTS } from "@/enums/ga4Events";

function transformPlatformOptsToFreeform(platformOpts: Record<string, unknown>) {
    return Object.entries(platformOpts).reduce<string[]>((acc, [key, value]) => {
        let stringValue: string;

        if (Array.isArray(value)) {
            stringValue = `${key}: [${value.join(", ")}]`;
        } else if (typeof value === "boolean") {
            if (!value) {
                return acc;
            }

            stringValue = `${key}`;
        } else if (typeof value === "string") {
            stringValue = `${key}: ${value}`;
        } else {
            stringValue = `${key}: ${value}`;
        }

        acc.push(stringValue);
        return acc;
    }, []);
}

export default defineComponent({
    components: {
        DownloadPlatformSelect,
        DownloadModalPlatformDetails,
        DownloadDisclaimer,
        DownloadHTMLOptions,
        DownloadVideoOptions,
        DownloadExcelOptions,
        DownloadAEVideoOptions
    },
    props: {
        deliverables: {
            required: true,
            type: Array as PropType<ConstructedDeliverable[]>
        },
        deliverableIdentifiers: {
            type: Array as PropType<string[]>
        },
        showHtmlOptions: {
            required: true,
            type: Boolean
        },
        showPsdOptions: {
            required: true,
            type: Boolean
        },
        showAeOptions: {
            required: true,
            type: Boolean
        }
    },

    emits: ["close"],

    setup() {
        const { clientId, campaignId } = useParams();
        const { computeQueryParams, getIsButtonDisabled, resetDownloadOptions } = useDownload();
        const { computeAEOutput } = useDownloadAEVideo();
        const ga4 = useGA4Tracking();

        return {
            clientId,
            campaignId,
            ga4,

            getIsButtonDisabled,

            computeQueryParams,
            resetDownloadOptions,
            computeAEOutput
        };
    },

    data() {
        return {
            platform: "" as DownloadFormatType | "",
            tab: "downloadOptions",
            DOWNLOAD_FORMAT,
            TemplateType,
            isCompleteEventTracked: false
        };
    },
    computed: {
        ...mapStores(useCreativeLibraryFiltersStore),

        canDownloadDeliverable() {
            return this.$auth.userCan(this.$auth.Actions.CanDownloadDeliverable, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        zipDownload() {
            return Object.values(ZIP_DOWNLOADS).includes(
                this.platform as (typeof ZIP_DOWNLOADS)[keyof typeof ZIP_DOWNLOADS]
            );
        },

        templateType() {
            if (this.showAeOptions) {
                return TemplateType.AE;
            }

            if (this.showPsdOptions) {
                return TemplateType.PSD;
            }

            return TemplateType.HTML5;
        },

        buttonDisabled() {
            return this.getIsButtonDisabled(this.platform, this.templateType);
        },

        platformOpts() {
            if (!this.platform) {
                return {};
            }
            return this.computeQueryParams(this.platform);
        }
    },

    watch: {
        canDownloadDeliverable() {
            if (!this.canDownloadDeliverable) {
                this.$snackbar.warning("You do not have access to download deliverables");
                this.close();
            }
        },

        platform(newVal: DownloadFormatType | "", oldVal: DownloadFormatType | "") {
            if (newVal && newVal !== oldVal) {
                this.ga4.trackFeatureEvent({
                    featureEvent: GA4_EVENT_TYPES.FEATURE_SETTINGS,
                    featureCategory: GA4_FEATURE_CATEGORIES.CREATIVE_LIBRARY_DOWNLOAD,
                    featureContext: `Download ${DownloadPlatformsLabels[newVal]}`
                });
            }

            this.resetDownloadOptions();
        }
    },

    mounted() {
        this.ga4.trackFeatureEvent({
            featureEvent: GA4_EVENT_TYPES.FEATURE_ACTIVATE,
            featureCategory: GA4_FEATURE_CATEGORIES.CREATIVE_LIBRARY_DOWNLOAD,
            featureContext: GA4_FEATURE_CONTEXTS.CREATIVE_LIBRARY_DOWNLOAD
        });
        this.resetDownloadOptions();
    },

    methods: {
        close() {
            if (!this.isCompleteEventTracked) {
                this.ga4.trackFeatureEvent({
                    featureEvent: GA4_EVENT_TYPES.FEATURE_COMPLETE,
                    featureCategory: GA4_FEATURE_CATEGORIES.CREATIVE_LIBRARY_DOWNLOAD,
                    featureContext: GA4_FEATURE_CONTEXTS.CANCEL_DOWNLOAD
                });
            }
            this.$emit("close");
        },

        onTabChange(tab: "downloadOptions" | "changesOverview") {
            this.tab = tab;
        },

        async processDownloads() {
            if (this.platform === "") {
                return;
            }

            if (this.platform === DOWNLOAD_FORMAT.VIDEO && this.showAeOptions) {
                try {
                    this.isCompleteEventTracked = true;
                    this.ga4.trackFeatureEvent({
                        featureEvent: GA4_EVENT_TYPES.FEATURE_COMPLETE,
                        featureCategory: GA4_FEATURE_CATEGORIES.CREATIVE_LIBRARY_DOWNLOAD,
                        featureContext: GA4_FEATURE_CONTEXTS.DOWNLOAD_SUCCESSFUL,
                        freeformValues: transformPlatformOptsToFreeform(this.platformOpts)
                    });

                    this.close();
                    const output = this.computeAEOutput();
                    const inputs = computeAfterEffectsLayers(this.deliverables);
                    const job = await createAEJob({
                        inputs,
                        user: {
                            id: this.$store.state.auth.me?.id as string,
                            name: this.$store.state.auth.me?.role.name as string
                        },
                        campaignId: this.campaignId,
                        output
                    });

                    const notificationId = await this.$store.dispatch(NotificationsAction.Add, {
                        id: job.jobId,
                        message: "Your ads are being prepared for download",
                        status: JobStatusToNotificationStatus[job.status],
                        type: NotificationTypes.PlainlyBatchRender,
                        job: {
                            _id: job.jobId,
                            message: "Your ads are being prepared for download",
                            status: JobStatusToNotificationStatus[job.status],
                            type: NotificationTypes.PlainlyBatchRender
                        }
                    });

                    this.$store.dispatch(JobsAction.SetNotificationIdByActiveJobId, {
                        jobId: job.jobId,
                        notificationId
                    });

                    this.$snackbar.info(
                        "Your ads are being prepared for download. Take a look at your notifications to check on progress"
                    );
                } catch {
                    this.$snackbar.error(
                        "There was an unexpected error while preparing your ads for download. Please try again in a few moments"
                    );
                }
            } else {
                this.isCompleteEventTracked = true;
                this.ga4.trackFeatureEvent({
                    featureEvent: GA4_EVENT_TYPES.FEATURE_COMPLETE,
                    featureCategory: GA4_FEATURE_CATEGORIES.CREATIVE_LIBRARY_DOWNLOAD,
                    featureContext: GA4_FEATURE_CONTEXTS.DOWNLOAD_SUCCESSFUL,
                    freeformValues: transformPlatformOptsToFreeform(this.platformOpts)
                });
                this.close();
                const jobNotificationMap = {
                    [JobNames.PUBFT_DOWNLOAD_EXCEL]: NotificationTypes.FlashtalkingDownloadExcel,
                    [JobNames.PUBFT_DOWNLOAD_ASSETS]: NotificationTypes.FlashtalkingDownloadAssets
                };

                try {
                    const job = await downloadJob(
                        this.campaignId,
                        this.platform,
                        this.deliverables,
                        this.deliverableIdentifiers,
                        this.creativeLibraryFiltersStore.getComputedLibraryFilters,
                        this.platformOpts
                    );

                    const notificationId = await this.$store.dispatch(NotificationsAction.Add, {
                        message: job.message,
                        status: JobStatusToNotificationStatus[job.status],
                        type:
                            jobNotificationMap[job.name as keyof typeof jobNotificationMap] ||
                            NotificationTypes.DownloadJob,
                        job
                    });

                    this.$store.dispatch(JobsAction.SetNotificationIdByActiveJobId, {
                        jobId: job._id,
                        notificationId
                    });

                    this.$snackbar.info(
                        "Your ads are being prepared for download. Take a look at your notifications to check on progress"
                    );
                } catch {
                    this.$snackbar.error(
                        "There was an unexpected error while preparing your ads for download. Please try again in a few moments"
                    );
                }
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.download-modal__button {
    margin: 0 0 0 $spacing-small;
}

.download-modal__options-wrapper {
    display: flex;
    flex-direction: column;
    gap: 7px;
}
</style>
